import React from 'react';
import {
  Action, ActionIcon, ActionIconProps, FormHandlers, ICON_TUNE, Portal, useModal,
} from '@doveit/bricks';
import { unionBy } from 'lodash';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import JobApplicationSerpFiltersForm, { JobApplicationSerpFiltersFormModel } from '../../components/job-application-serp-filters-form/JobApplicationSerpFiltersForm';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { jobApplicationSerpFiltersFormModelToAdvancedFilters, serpJobApplicationAdvancedFiltersMapper } from './mappers';
import { jobApplicationAssigneeUsers, jobApplicationHrUsers } from '../../constants';
import { CustomUser, UserRole } from '../../../types';
import { boolToBooleanString } from '../../../utils/boolean-string/booleanString';
import { formatInputDate } from '../../../utils/form';
import useFilters from '../../../hooks/use-filters/useFilters';
import { SerpJobApplicationAdvancedFilters } from './types';

export interface ChildFnProps {
  openModal: VoidFunction,
}

export interface FilterSerpJobApplicationsActionProps extends React.AriaAttributes {
  hasSubordinates?: boolean,
  size?: ActionIconProps['size'],
  children?: (props: ChildFnProps) => React.ReactNode,
}

const FilterSerpJobApplicationsAction: React.FC<FilterSerpJobApplicationsActionProps> = ({
  hasSubordinates = true,
  size,
  children,
  ...rest
}) => {
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const modal = useModal();

  const {
    user, userIsAgent, userIsHR, userIsAdmin,
  } = useRBAC();

  const {
    filters: params,
    replaceFilters,
  } = useFilters<SerpJobApplicationAdvancedFilters>(serpJobApplicationAdvancedFiltersMapper);

  const assigneeAndInterviewers = React.useMemo(() => unionBy(
    (params.assigneeEmail || []),
    (params.interviewerEmail || []),
    (v) => v.toString(),
  ), [params.assigneeEmail, params.interviewerEmail]);

  const supervisorEmails = React.useMemo(() => {
    if (params.supervisorEmail?.length) return params.supervisorEmail.map((param) => param.email);

    const currentUserIsHr = jobApplicationHrUsers.some((u) => u.email === user.email);
    return currentUserIsHr ? [user.email] : [];
  }, [params.supervisorEmail, user.email]);

  const assigneeInterviewersWithRole = React.useMemo(() => {
    const agents = assigneeAndInterviewers
      .filter((u) => !jobApplicationHrUsers.some((hr) => hr.email === u.email) && !jobApplicationAssigneeUsers.some((a) => a.email === u.email));

    const normalizedAgents = agents.map((agent) => ({
      ...agent,
      name: agent.shortName || agent.email,
      'https://backoffice.dove.it/roles': [UserRole.AGENT],
    } as unknown as CustomUser));

    if (userIsAgent) return normalizedAgents;

    const hrs = jobApplicationHrUsers.map((u) => ({
      ...u,
      'https://backoffice.dove.it/roles': [UserRole.HR],
    } as unknown as CustomUser));

    const admins = jobApplicationAssigneeUsers.map((u) => ({
      ...u,
      'https://backoffice.dove.it/roles': [UserRole.ADMIN],
    } as unknown as CustomUser));

    const allUsers = [
      ...normalizedAgents,
      ...hrs.filter((u) => assigneeAndInterviewers.some((a) => a.email === u.email)),
      ...admins.filter((u) => assigneeAndInterviewers.some((a) => a.email === u.email)),
    ];

    const usersByEmail = new Map(allUsers.map((u) => [u.email, u]));

    return assigneeAndInterviewers.map((v) => {
      const foundUser = usersByEmail.get(v.email);
      if (foundUser) return foundUser as CustomUser;
      return {
        ...v,
        name: v.shortName || v.email,
        'https://backoffice.dove.it/roles': [],
      } as unknown as CustomUser;
    });
  }, [assigneeAndInterviewers, userIsAgent]);

  const initialValues = React.useMemo(() => ({
    type: params.type,
    jobPositionIds: params.jobPositionId,
    provinces: params.province,
    supervisorEmails,
    assigneeInterviewers: assigneeInterviewersWithRole,
    createdAtFrom: params.createdAtGt ? formatInputDate(new Date(params.createdAtGt)) : undefined,
    createdAtTo: params.createdAtLt ? formatInputDate(new Date(params.createdAtLt)) : undefined,
    source: params.source,
    isFranchising: boolToBooleanString(params.isFranchising),
    hasLicense: boolToBooleanString(params.hasLicense),
  }), [assigneeInterviewersWithRole, params.createdAtGt, params.createdAtLt, params.hasLicense, params.isFranchising, params.jobPositionId, params.province, params.source, params.type, supervisorEmails]);

  const handleSubmit = React.useCallback((values: JobApplicationSerpFiltersFormModel) => {
    const newFilters = jobApplicationSerpFiltersFormModelToAdvancedFilters(values);
    replaceFilters(newFilters, { deleteKeys: ['page'] });

    modal.close();
  }, [modal, replaceFilters]);

  const hasActiveFilters = React.useMemo(() => (
    Object.values(params).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null;
    })
  ), [params]);

  if (!(userIsAdmin || userIsHR || userIsAgent)) {
    return null;
  }

  return (
    <>
      {
        children
          ? children({
            openModal: () => modal.open(),
          })
          : (
            <ActionIcon
              icon={{ path: ICON_TUNE }}
              aria-label="Azione per filtrare le candidature"
              label="Filtra candidature"
              onClick={() => modal.open()}
              color={hasActiveFilters ? 'primary' : 'neutral'}
              size={size}
              {...rest}
            />
          )
      }
      <Portal>
        <SimpleModal
          {...modal}
          title="Filtra candidature"
          aria-label="Modale per filtrare le candidature"
          footer={(
            <Action
              label="Filtra"
              color="primary"
              emphasis="high"
              onClick={() => formRef.current.handleSubmit()}
            />
          )}
        >
          <JobApplicationSerpFiltersForm
            innerRef={formRef}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            allowedAssigneeType={userIsAgent ? 'agents' : 'all'}
            agentsCoordinator={userIsAgent ? user.email : undefined}
            hiddenFields={{
              type: userIsAgent,
              supervisorEmails: userIsAgent,
              source: userIsAgent,
              isFranchising: userIsAgent,
              assigneeInterviewers: userIsAgent && !hasSubordinates,
            }}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default FilterSerpJobApplicationsAction;
